'use client'

import Image from 'next/image'
import type { CSSProperties } from 'react'
import { useTranslations } from 'next-intl'
import { useOpenStatusLabel } from './OpenStatusBadge'
import type { OpenStatus } from '@/lib/hours/openStatus'

/** Divisor de fases de luna (nueva → llena → nueva) — firma del tema
 *  medianoche. Cinco puntos en el acento flanqueados por hairlines. */
function Moons({ className = '' }: { className?: string }) {
  const dot = (style: CSSProperties, key: number) => (
    <span
      key={key}
      className="block w-[9px] h-[9px] rounded-full"
      style={{ border: '1px solid color-mix(in srgb, var(--ms-accent) 75%, transparent)', ...style }}
    />
  )
  return (
    <div className={`flex items-center gap-3.5 ${className}`} aria-hidden>
      <span
        className="block h-px w-14 sm:w-[70px]"
        style={{ background: 'linear-gradient(to right, transparent, color-mix(in srgb, var(--ms-accent) 55%, transparent))' }}
      />
      {dot({}, 0)}
      {dot({ background: 'linear-gradient(90deg, var(--ms-accent) 50%, transparent 50%)' }, 1)}
      {dot({ background: 'var(--ms-accent)' }, 2)}
      {dot({ background: 'linear-gradient(270deg, var(--ms-accent) 50%, transparent 50%)' }, 3)}
      {dot({}, 4)}
      <span
        className="block h-px w-14 sm:w-[70px]"
        style={{ background: 'linear-gradient(to left, transparent, color-mix(in srgb, var(--ms-accent) 55%, transparent))' }}
      />
    </div>
  )
}

type Props = {
  businessName: string
  tagline?: string
  heroImageUrl?: string
  heroVideoUrl?: string
  city?: string | null
  pickupWaitMinutes?: number
  readyInLabel?: string
  onViewMenu: () => void
  onDelivery?: () => void
  hasDelivery?: boolean
  openStatus?: OpenStatus | null
}

/**
 * Hero del tema "medianoche" (navy grabado): oyster bar de noche. Marco DOBLE
 * de invitación grabada pegado a los bordes, nombre en Cinzel con tracking
 * ancho, divisor de fases de luna y glow frío de luz de luna (no del acento).
 * Si hay foto/video va a sangre bajo el overlay del token. El CTA principal es
 * champán (acento) con texto navy del fondo. Lee tokens del tema (--ms-*).
 */
export function HeroMedianoche({
  businessName,
  tagline,
  heroImageUrl,
  heroVideoUrl,
  city,
  pickupWaitMinutes,
  readyInLabel,
  onViewMenu,
  onDelivery,
  hasDelivery,
  openStatus,
}: Props) {
  const t = useTranslations('public.microsite')
  const statusLabel = useOpenStatusLabel(openStatus ?? null)
  const hasMedia = !!(heroImageUrl || heroVideoUrl)

  return (
    <section className="relative w-full overflow-hidden" style={{ background: 'var(--ms-bg)' }}>
      {/* Media a sangre (opcional) */}
      {heroVideoUrl ? (
        <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" src={heroVideoUrl} />
      ) : heroImageUrl ? (
        <Image src={heroImageUrl} alt={businessName} fill priority sizes="100vw" className="object-cover" />
      ) : null}
      {hasMedia && <div className="absolute inset-0 z-10" style={{ background: 'var(--ms-hero-overlay)' }} />}

      {/* Luz de luna fría desde arriba — deliberadamente NO usa el acento */}
      <div
        className="absolute inset-0 z-10 pointer-events-none"
        style={{ background: 'radial-gradient(90% 60% at 50% -8%, rgba(214,225,240,0.14), transparent 52%)' }}
      />

      {/* Marco doble grabado — firma del tema */}
      <div
        className="absolute inset-3 sm:inset-4 z-20 pointer-events-none"
        style={{ border: '1px solid color-mix(in srgb, var(--ms-accent) 45%, transparent)' }}
        aria-hidden
      >
        <span
          className="absolute"
          style={{ inset: 5, border: '1px solid color-mix(in srgb, var(--ms-accent) 20%, transparent)' }}
        />
      </div>

      {/* Contenido centrado */}
      <div
        className="relative z-30 mx-auto flex max-w-3xl flex-col items-center justify-center px-7 text-center"
        style={{ minHeight: 'min(680px, 88vh)' }}
      >
        {city && (
          <span className="text-[11px] font-semibold uppercase tracking-[0.44em] pl-[0.44em]" style={{ color: 'var(--ms-accent)' }}>
            {city}
          </span>
        )}

        <h1
          className="mt-6 uppercase leading-[1.06] tracking-[0.2em] indent-[0.2em] text-[clamp(38px,8.5vw,88px)]"
          style={{
            fontFamily: 'var(--ms-font-display)',
            fontWeight: 600,
            color: 'var(--ms-text)',
            textShadow: hasMedia ? '0 6px 44px rgba(0,0,0,0.7)' : undefined,
          }}
        >
          {businessName}
        </h1>

        {tagline && (
          <p className="mt-5 max-w-xl text-[15.5px] sm:text-[17px] leading-relaxed" style={{ color: 'var(--ms-text-soft)' }}>
            {tagline}
          </p>
        )}

        <Moons className="mt-7" />

        {/* Meta discreta */}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[12.5px] tracking-wide" style={{ color: 'var(--ms-text-muted)' }}>
          {statusLabel && (
            <span className="inline-flex items-center gap-2">
              <span
                className="w-[6px] h-[6px] rounded-full"
                style={{
                  background:
                    statusLabel.tone === 'open'
                      ? '#5fcf80'
                      : statusLabel.tone === 'soon'
                        ? '#f59e0b'
                        : 'var(--ms-text-muted)',
                }}
              />
              {statusLabel.text}
            </span>
          )}
          {statusLabel && <span aria-hidden>·</span>}
          <span>{t('pickupAvailable')}</span>
          {pickupWaitMinutes ? (
            <>
              <span aria-hidden>·</span>
              <span>{readyInLabel ?? t('readyIn', { minutes: pickupWaitMinutes })}</span>
            </>
          ) : null}
        </div>

        {/* CTAs: champán con texto navy + outline fino */}
        <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={onViewMenu}
            className="inline-flex items-center px-10 py-4 text-[12px] font-bold uppercase tracking-[0.22em] transition hover:brightness-105 active:scale-[0.98]"
            style={{
              background: 'var(--ms-accent)',
              color: 'var(--ms-bg)',
              borderRadius: 'var(--ms-radius)',
              boxShadow: '0 20px 48px -18px rgba(0,0,0,0.85)',
            }}
          >
            {t('orderOnline')}
          </button>
          {hasDelivery && onDelivery && (
            <button
              type="button"
              onClick={onDelivery}
              className="inline-flex items-center px-7 py-4 text-[11.5px] font-semibold uppercase tracking-[0.22em] transition hover:bg-white/5"
              style={{
                color: 'var(--ms-text)',
                borderRadius: 'var(--ms-radius)',
                border: '1px solid color-mix(in srgb, var(--ms-accent) 45%, transparent)',
              }}
            >
              {t('delivery')}
            </button>
          )}
        </div>
      </div>
    </section>
  )
}
